// Statements: the SAP factors the engine uses and the pre-planning benchmarks. A house type with no SAP for the site
// (source 'benchmark') gets typical figures for its built form and floor area under the chosen Part L edition, so a
// pre-planning statement can be written from a layout and a house mix. They are estimates, stated as such in the
// report with a tolerance, and the full SAP replaces them before commencement. See docs/STATEMENTS.md.
import type { PartLEdition, SapFigures, StatementHouseType } from './statements'

// ---------------------------------------------------------------- factors (SAP 10.2 Table 12)

/** kgCO2/kWh. Mains gas and grid electricity (annual; the SAP calc itself uses monthly electricity factors). */
export const GAS_CO2 = 0.21
export const ELEC_CO2 = 0.136
/** Primary energy factors. */
export const GAS_PEF = 1.13
export const ELEC_PEF = 1.501
/** PV generates mostly in summer, when the SAP 10.2 monthly electricity factors are lowest, so it is credited at 85%
 *  of the annual factor rather than all of it. Keeps the estimated DER on the cautious side. */
export const PV_CREDIT = 0.85
/** Primary energy of exported electricity (SAP 10.2 Table 12), and the share of PV generation a home without a battery
 *  uses itself (SAP 10.2 Appendix M puts it around 0.4–0.6; the lower half is taken). The rest is exported. */
export const ELEC_EXPORT_PEF = 0.501
export const PV_SELF_USE = 0.45
/** kWh/yr per kWp where SAP gives no generation: a cautious UK average for a south-ish roof (SAP Appendix U runs
 *  about 800–950 by region and orientation). */
export const PV_YIELD_KWH_PER_KWP = 850
/** Roof needed per kWp with current 400–450 W modules and spacing at the edges, m². */
export const PV_M2_PER_KWP = 5

// ---------------------------------------------------------------- Part L notional dwellings

/** Part L 2021 notional dwelling: mains gas boiler (89.5%), PV of 40% of the ground floor area / 6.5 kWp. */
export const L2021_NOTIONAL_BOILER_EFF = 0.895
export const L2021_NOTIONAL_PV_KWP_PER_M2 = 0.4 / 6.5
/** Part L 2026 (Future Homes Standard) notional dwelling: heat pump at 250% (SAP 10.3), PV to L3. */
export const L2026_NOTIONAL_HP_EFF = 2.5
/** Part L 2026 L3: at least the output of 0.22 kWp/m² over 40% of the ground floor area (SAP 10.3 Appendix M1). */
export const L3_KWP_PER_M2 = 0.22
export const L3_AREA_FRACTION = 0.4
/** Part L 2021 is about 31% below Part L 2013 (CO2), for requirements still measured against 2013. */
export const L2021_BELOW_L2013 = 0.31

/** The benchmark proposal: an air source heat pump, seasonal efficiency for space heating and for hot water (typical
 *  PCDB values at a 45–50 °C flow; cautious). */
export const ASHP_SCOP_SPACE = 2.8
export const ASHP_SCOP_WATER = 2.4

/** Typical peak demand of an all-electric heat-pump home after diversity, kVA, for the grid check when the Grid tool
 *  has no figure of its own (DNO design allowances run about 3–4 kVA). */
export const ADMD_ALL_ELECTRIC_KVA = 3.5

/** Benchmark tolerance, ± fraction, by edition (SAP 10.3 is newer and its factors are not settled, so wider). */
export const BENCHMARK_TOLERANCE: Record<PartLEdition, number> = { L2021: 0.15, L2026: 0.25 }

// ---------------------------------------------------------------- the benchmark table

type Form = 'detached' | 'semi' | 'mid-terrace' | 'bungalow' | 'flat' | 'maisonette'

/**
 * Typical SAP 10.2 space heating demand (box 98 / TFA) and target fabric energy efficiency, kWh/m²/yr, for a dwelling
 * built to the Part L 2021 notional fabric (walls 0.18, roof 0.11, floor 0.13, windows 1.2 W/m²K, 5 m³/m²h), at a
 * reference floor area. The Future Homes Standard keeps essentially the same fabric, so the table serves both
 * editions (a little high for 2026, which is the cautious side for space heating and EUI policies). Flats are an
 * average of ground, middle and top floor positions. Per-m² demand rises in smaller homes (more exposed surface per
 * m² of floor): see `areaFactor`.
 */
const TABLE: Record<Form, { refTfa: number; spaceHeating: number; tfee: number }> = {
  detached: { refTfa: 120, spaceHeating: 45, tfee: 48 },
  semi: { refTfa: 85, spaceHeating: 40, tfee: 43 },
  'mid-terrace': { refTfa: 80, spaceHeating: 32, tfee: 35 },
  bungalow: { refTfa: 70, spaceHeating: 52, tfee: 55 },
  flat: { refTfa: 60, spaceHeating: 28, tfee: 31 },
  maisonette: { refTfa: 75, spaceHeating: 32, tfee: 35 },
}

/** Lighting (LED, SAP 10.2) kWh/m²/yr and pumps and fans (heating circulation + dMEV) kWh/yr per dwelling. */
const LIGHTING_KWH_M2 = 2.5
const PUMPS_FANS_KWH = 150
/** Hot water cylinder and primary losses with a heat pump, kWh/yr (the notional gas combi has none). */
const CYLINDER_LOSS_KWH = 300

const pvCo2 = (kwh: number) => kwh * ELEC_CO2 * PV_CREDIT
const pvPrimary = (kwh: number) => kwh * (PV_SELF_USE * ELEC_PEF * PV_CREDIT + (1 - PV_SELF_USE) * ELEC_EXPORT_PEF)

function formOf(ht: StatementHouseType): Form {
  if (ht.propertyType === 'flat') return 'flat'
  if (ht.propertyType === 'maisonette') return 'maisonette'
  if (ht.propertyType === 'bungalow') return 'bungalow'
  if (ht.builtForm === 'detached') return 'detached'
  if (ht.builtForm === 'mid-terrace') return 'mid-terrace'
  return 'semi'
}

/** Per-m² demand scales gently with size against the reference floor area, within ±25%. */
function areaFactor(tfa: number, refTfa: number): number {
  if (!(tfa > 0)) return 1
  return Math.min(1.25, Math.max(0.8, Math.pow(refTfa / tfa, 0.25)))
}

/** SAP occupancy from floor area (SAP 10.2 Table 1b). */
export function sapOccupancy(tfa: number): number {
  if (tfa <= 13.9) return 1
  const x = tfa - 13.9
  return 1 + 1.76 * (1 - Math.exp(-0.000349 * x * x)) + 0.0013 * x
}

/** Hot water heat demand, kWh/yr: 25N + 36 litres/day heated by 37 K, plus 15% distribution losses. */
export function hotWaterKwh(tfa: number): number {
  const litres = 25 * sapOccupancy(tfa) + 36
  return ((litres * 365 * 4.18 * 37) / 3600) * 1.15
}

/** Ground floor area for L3 PV: the given figure, else TFA over the storeys (flats: their share of a 3-storey block). */
export function groundFloorAreaOf(ht: StatementHouseType): { area: number; estimated: boolean } {
  if (ht.groundFloorArea != null && ht.groundFloorArea > 0) return { area: ht.groundFloorArea, estimated: false }
  const storeys = ht.storeys && ht.storeys > 0 ? ht.storeys : ht.propertyType === 'bungalow' ? 1 : ht.propertyType === 'flat' ? 3 : 2
  return { area: ht.tfa / storeys, estimated: true }
}

/** Part L 2026 L3 minimum PV for one dwelling, kWp. */
export function l3MinimumKwp(groundFloorArea: number): number {
  return L3_KWP_PER_M2 * L3_AREA_FRACTION * Math.max(0, groundFloorArea)
}

/**
 * Benchmark SAP figures for a house type with no SAP of its own. Built from components rather than read off a
 * table, so the floor area, ground floor area and PV move the figures the way SAP would:
 *   space heating   table above × areaFactor
 *   hot water       hotWaterKwh (25N + 36 l/day)
 *   lighting, pumps and fans as above
 *   TER  Part L 2021: notional gas boiler 89.5% + notional PV (40% GFA / 6.5);  Part L 2026: notional heat pump 250%
 *        + PV to L3
 *   DER  air source heat pump (2.8 space / 2.4 water) + PV: `pvKwp` when given, else the edition's notional PV
 *   DFEE = TFEE (fabric at the notional specification: no Be Lean saving is claimed for an estimate)
 * Carbon at SAP 10.2 factors for both editions; SAP 10.3's lower electricity factor would give lower rates. PV is
 * credited at 85% of the carbon factor; for primary energy the exported share counts at the export factor. Rates are
 * not taken below zero: a benchmark does not claim a net-exporting dwelling.
 */
export function benchmarkSap(ht: StatementHouseType, partL: PartLEdition, pvKwp?: number | null): SapFigures {
  const tfa = Math.max(1, ht.tfa)
  const row = TABLE[formOf(ht)]
  const k = areaFactor(tfa, row.refTfa)
  const spaceHeating = row.spaceHeating * k
  const tfee = row.tfee * k
  const shKwh = spaceHeating * tfa
  const dhwKwh = hotWaterKwh(tfa)
  const auxKwh = LIGHTING_KWH_M2 * tfa + PUMPS_FANS_KWH
  const gfa = groundFloorAreaOf(ht).area
  const notionalPvKwp = partL === 'L2026' ? l3MinimumKwp(gfa) : L2021_NOTIONAL_PV_KWP_PER_M2 * gfa
  const notionalPvKwh = notionalPvKwp * PV_YIELD_KWH_PER_KWP

  let terKg: number, tperKwh: number
  if (partL === 'L2021') {
    const gas = (shKwh + dhwKwh) / L2021_NOTIONAL_BOILER_EFF
    terKg = gas * GAS_CO2 + auxKwh * ELEC_CO2 - pvCo2(notionalPvKwh)
    tperKwh = gas * GAS_PEF + auxKwh * ELEC_PEF - pvPrimary(notionalPvKwh)
  } else {
    const elec = (shKwh + dhwKwh + CYLINDER_LOSS_KWH) / L2026_NOTIONAL_HP_EFF + auxKwh
    terKg = elec * ELEC_CO2 - pvCo2(notionalPvKwh)
    tperKwh = elec * ELEC_PEF - pvPrimary(notionalPvKwh)
  }

  const kwp = pvKwp != null && pvKwp >= 0 ? pvKwp : notionalPvKwp
  const pvKwh = kwp * PV_YIELD_KWH_PER_KWP
  const regulatedKwh = shKwh / ASHP_SCOP_SPACE + (dhwKwh + CYLINDER_LOSS_KWH) / ASHP_SCOP_WATER + auxKwh
  const derKg = regulatedKwh * ELEC_CO2 - pvCo2(pvKwh)
  const dperKwh = regulatedKwh * ELEC_PEF - pvPrimary(pvKwh)

  const rate = (v: number) => Math.max(0, v / tfa)
  return {
    ter: rate(terKg),
    der: rate(derKg),
    tfee,
    dfee: tfee,
    tper: rate(tperKwh),
    dper: rate(dperKwh),
    spaceHeating,
    regulatedKwh,
    pvKwp: kwp,
    pvKwh,
    heating: 'Air source heat pump (benchmark)',
  }
}
